import React, { FC } from "react"
import { Link } from "gatsby"
import styled from "styled-components"

import TeamMember from "./TeamMember"
import ServicesCards from "../services/services-cards"
import { ResourcesList } from "../../pages/resources"
import CustomPrismicTitle from "../atoms/CustomPrismicTitle"
import DeepdiveCards from "../deepdive/deepdive-cards"
import Button from "../atoms/Button"
import CaseStudyCard, { ICaseStudy } from "../cases/cases-cards/CaseStudyCard"

// Types
import { IServiceCard } from "../../prismic-types/slices/services-cards"
import { IResource } from "../../prismic-types/resources"

// Hooks
import { useLocale } from "../../hooks/useLocale"

// Styles
import colors from "../../styles/colors"
import { fonts } from "../../styles/fonts"
import { fontWeights } from "../../styles/textStyles"
import mq from "../../styles/breakpoints"

const CTASlice: FC<any> = ({ slice }) => {
  const { isDutch, getLocalizedLink } = useLocale()

  const teamMemberSlice =
    slice.slice_type === "team_member_v2" ? slice : undefined
  const servicesCardsSlice =
    slice.slice_type === "services_cards" ? slice : undefined
  const resourcesCardsSlice =
    slice.slice_type === "resources_cards" ? slice : undefined
  const deepdiveCardsSlice =
    slice.slice_type === "deep_dive_cards" ? slice : undefined
  const caseStudiesCardsSlice =
    slice.slice_type === "case_studies_cards" ? slice : undefined

  if (!!teamMemberSlice)
    return (
      <TeamMember
        key={teamMemberSlice.id}
        heading={teamMemberSlice.primary.heading1}
        description={teamMemberSlice.primary.description1}
        image={teamMemberSlice.primary.team_member.document.data.photo.fluid}
        name={teamMemberSlice.primary.team_member.document.data.name}
        role={teamMemberSlice.primary.team_member.document.data.role}
      />
    )
  if (!!servicesCardsSlice)
    return (
      <ServicesCards
        title={servicesCardsSlice.primary.services_cards_heading.raw}
        button={{
          label:
            servicesCardsSlice.primary.services_cards_button_label ||
            (isDutch ? "Alle diensten" : "All services"),
          href:
            servicesCardsSlice.primary.services_cards_button_link ||
            "/services",
        }}
        cards={servicesCardsSlice.items
          .filter((item: any) => item.services_cards_template.document)
          .map((item: any) => {
            const docData = item.services_cards_template?.document?.data
            const docUid = item.services_cards_template?.document?.uid
            if (!docData || !docUid) return null

            return {
              heading: docData.heading,
              title: docData.title,
              description: docData.description,
              image: docData.thumbnail_image,
              serviceLevel: docData.service_level,
              serviceType: docData.service_type,
              serviceTypeColor: docData.service_type_color,
              button: {
                label: isDutch ? "Meer weten" : "Learn more",
                href: `/services/${docUid}`,
              },
            } as IServiceCard
          })
          .filter((item: any) => !!item)}
      />
    )
  if (!!resourcesCardsSlice)
    return (
      <ResourcesConatiner>
        <HeaderSection>
          {resourcesCardsSlice.primary?.resources_cards_heading?.raw && (
            <div>
              <CustomPrismicTitle
                data={resourcesCardsSlice.primary.resources_cards_heading.raw}
              />
            </div>
          )}
          <StyledButtonFirst
            fullWidth={false}
            href={getLocalizedLink(
              resourcesCardsSlice.primary.resources_cards_button_link ||
                "/resources"
            )}
            label={
              resourcesCardsSlice.primary.resources_cards_label ||
              (isDutch ? "Alle resources" : "All resources")
            }
            variant="secondary"
          />
        </HeaderSection>
        {resourcesCardsSlice && (
          <ResourcesList
            allowDownload={false}
            showDownloadButton={false}
            resources={resourcesCardsSlice?.items
              ?.map((item: any) => {
                const resourceDocument = item.resources_cards_template.document
                if (!resourceDocument) return null // In case if reference initiates but didn't attach reference document.
                return {
                  slug: resourceDocument.uid,
                  title: resourceDocument.data.heading.text,
                  text: resourceDocument.data.description.text,
                  image: {
                    alt: resourceDocument.data?.image?.alt,
                    url: resourceDocument.data?.image?.fluid,
                    imageUrl: resourceDocument.data?.image?.url,
                  },
                } as IResource
              })
              .filter((item: any) => !!item)}
          />
        )}
      </ResourcesConatiner>
    )
  if (!!deepdiveCardsSlice)
    return (
      <DeepdiveCards
        key={deepdiveCardsSlice.slice_type}
        title={deepdiveCardsSlice.primary.deep_dive_heading.raw}
        description={deepdiveCardsSlice.primary.deep_dive_description}
        button={{
          label:
            deepdiveCardsSlice.primary.button_label ||
            (isDutch ? "Alle deep dives" : "All deep dives"),
          href: deepdiveCardsSlice.primary.button_link || "/deepdives",
        }}
        cards={deepdiveCardsSlice.items
          .filter((item: any) => item.deep_dive_template?.document)
          .map((item: any) => {
            const docData = item.deep_dive_template?.document?.data
            const docUid = item.deep_dive_template?.uid
            if (!docData || !docUid) return null

            return {
              label: docData.heading.text,
              description: docData.description,
              link: {
                label: isDutch ? "Meer weten" : "Learn more",
                href: `/deepdive/${docUid}`,
                color: docData.heading_color,
              },
              backgroundColor: docData.background_color,
              foregroundColor: docData.foreground_color,
            }
          })
          .filter((item: any) => !!item)}
        isLandingPage={false}
      />
    )
  if (!!caseStudiesCardsSlice) {
    const relatedCases: ICaseStudy[] = (caseStudiesCardsSlice?.items || [])
      .map((item: any) => {
        const doc = item.case_studies_template?.document
        if (!doc) return null
        return {
          slug: doc.uid,
          title: doc.data.title?.text || "",
          companyName: doc.data.company_name || "",
          companySector: doc.data.company_sector || "",
          imageUrl: doc.data.hero_image?.url || "",
        }
      })
      .filter(Boolean)

    if (relatedCases.length === 0) return null

    const sectionTitle =
      caseStudiesCardsSlice.primary?.case_studies_heading?.text || ""
    const buttonLabel =
      caseStudiesCardsSlice.primary?.button_label ||
      (isDutch ? "Alle case studies" : "All case studies")

    return (
      <MoreCaseStudies>
        <MoreHeader>
          <MoreTitle>{sectionTitle}</MoreTitle>
          <AllCasesButton to={getLocalizedLink("/cases")}>
            {buttonLabel}
          </AllCasesButton>
        </MoreHeader>
        <MoreGrid>
          {relatedCases
            .slice(0, 3)
            .map((caseStudy: ICaseStudy, cardIdx: number) => (
              <MoreCardWrapper
                key={caseStudy.slug}
                cardCount={Math.min(relatedCases.length, 3)}
                cardIndex={cardIdx}
              >
                <CaseStudyCard caseStudy={caseStudy} />
              </MoreCardWrapper>
            ))}
        </MoreGrid>
      </MoreCaseStudies>
    )
  }

  return null
}

export default CTASlice

const ResourcesConatiner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledButtonFirst = styled(Button)`
  display: none;

  ${mq.from.XSM`
    font-weight: ${fontWeights.medium};
    display: flex;
    justify-content: center;
    align-items: center;
  `}
`

const MoreCaseStudies = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const MoreHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const MoreTitle = styled.h2`
  margin: 0;
  font-family: ${fonts.primary};
  font-size: 28px;
  font-weight: ${fontWeights.bold};
  line-height: 36px;
  letter-spacing: -1px;
  color: ${colors.main["100"]};

  ${mq.from.M`
    font-size: 40px;
    line-height: 52px;
  `}
`

const AllCasesButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  background-color: rgba(111, 63, 243, 0.15);
  border-radius: 8px;
  box-shadow: 0px 1px 0px 0px rgba(42, 9, 134, 0.1);
  font-family: ${fonts.primary};
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  line-height: 20px;
  color: ${colors.purpleLight};
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 240ms ease-in-out;

  &:hover {
    background-color: ${colors.purpleLight};
    color: ${colors.white};
  }

  ${mq.from.M`
    font-size: 18px;
  `}
`

const MoreGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mq.from.M`
    flex-direction: row;
    align-items: center;
    gap: 32px;
  `}
`

const MoreCardWrapper = styled.div<{ cardCount: number; cardIndex: number }>`
  width: 100%;

  ${mq.from.M`
    ${({ cardCount, cardIndex }: { cardCount: number; cardIndex: number }) => {
      if (cardCount === 1) {
        return `
          flex: 1 0 0;
          min-width: 0;
        `
      }
      if (cardCount === 2) {
        return cardIndex === 0
          ? `
            flex: 1 0 0;
            min-width: 0;
          `
          : `
            flex: 0 0 411px;
            width: 411px;
          `
      }
      return `
        flex: 1 0 0;
        min-width: 0;
      `
    }}
  `}
`
